import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Building2, RefreshCw } from 'lucide-react';
import { SwapStatus } from './SwapForm';
import { BankMasterCard, BankLedger } from './AdminBankParts';
import { OutboundFeedPanel } from './OutboundFeedPanel';
import { StatementEmailsAdmin } from './StatementEmailsAdmin';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/pas-bank/admin`;
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const SubRow = ({ s, act, onDeposit }) => {
  const a = s.account;
  return (
    <tr className="border-t border-slate-700" data-testid={`bank-sub-${s.client_id}`}>
      <td className="py-2 text-xs">{s.name || s.email}<p className="text-slate-500">{s.email}</p></td>
      <td className="text-xs font-mono">{a ? `${a.account_number} / ${a.routing_number}` : '—'}</td>
      <td className="text-xs">{a ? money(a.bank_balance) : '—'}</td>
      <td className="text-xs">{a ? money(a.beth_balance) : '—'}</td>
      <td className="text-xs">{a ? <SwapStatus s={a.status} /> : <span className="text-slate-500">No account</span>}</td>
      <td className="space-x-1 whitespace-nowrap text-right">
        {!a && <button onClick={() => act(`clients/${s.client_id}/open`)} className="px-2 py-1 bg-emerald-600 text-white rounded text-xs" data-testid={`bank-open-${s.client_id}`}>Open account</button>}
        <button onClick={() => onDeposit(s)} className="px-2 py-1 bg-cyan-600 text-white rounded text-xs" data-testid={`bank-deposit-${s.client_id}`}>Deposit</button>
        {a && <button onClick={() => act(`clients/${s.client_id}/sync`)} className="px-2 py-1 bg-slate-600 text-white rounded text-xs" data-testid={`bank-sync-${s.client_id}`}>Sync</button>}
        {a && <button onClick={() => act(`clients/${s.client_id}/freeze`, { frozen: a.status !== 'frozen' })} className={`px-2 py-1 ${a.status === 'frozen' ? 'bg-emerald-700' : 'bg-red-600'} text-white rounded text-xs`} data-testid={`bank-freeze-${s.client_id}`}>{a.status === 'frozen' ? 'Unfreeze' : 'Freeze'}</button>}
      </td>
    </tr>
  );
};

const DepositModal = ({ sub, subscribers, token, onClose, onDone }) => {
  const [amount, setAmount] = useState('');
  const [memo, setMemo] = useState('Admin deposit');
  const [selectedId, setSelectedId] = useState(sub?.client_id || '');
  const [busy, setBusy] = useState(false);
  const target = sub || (subscribers || []).find(x => x.client_id === selectedId);
  const submit = async () => {
    const amt = parseFloat(amount);
    if (!target) { toast.error('Choose a subscriber'); return; }
    if (!amt || amt <= 0) { toast.error('Enter an amount'); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/clients/${target.client_id}/deposit`, { amount_usd: amt, memo },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(r.data.message); onDone(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail?.toString() || 'Failed'); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose} data-testid="bank-deposit-modal">
      <div className="bg-slate-900 border border-cyan-500/40 rounded-xl p-5 w-full max-w-md space-y-3" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-white font-semibold">Deposit BETH USD{target ? ` to ${target.name || target.email}` : ''}</h3>
        <p className="text-xs text-slate-400">Mints BETH USD to the subscriber's wallet on Base; their bank balance mirrors it 1:1 once confirmed on-chain. Sandbox/test until a live bank partnership is active.</p>
        {!sub && (
          <div>
            <label className="text-xs text-slate-400">Subscriber</label>
            <select value={selectedId} onChange={(e) => setSelectedId(e.target.value)} className="w-full mt-1 px-3 py-2 bg-slate-950 border border-slate-600 rounded text-white text-sm" data-testid="bank-deposit-subscriber">
              <option value="">Select subscriber…</option>
              {(subscribers || []).filter(x => x.wallet_address).map(x => <option key={x.client_id} value={x.client_id}>{x.name || x.email}</option>)}
            </select>
          </div>
        )}
        <div>
          <label className="text-xs text-slate-400">Amount (USD)</label>
          <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" className="w-full mt-1 px-3 py-2 bg-slate-950 border border-slate-600 rounded text-white text-sm" data-testid="bank-deposit-amount" />
        </div>
        <div>
          <label className="text-xs text-slate-400">Memo</label>
          <input value={memo} onChange={(e) => setMemo(e.target.value)} className="w-full mt-1 px-3 py-2 bg-slate-950 border border-slate-600 rounded text-white text-sm" data-testid="bank-deposit-memo" />
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose} className="px-3 py-2 bg-slate-700 text-white rounded text-sm">Cancel</button>
          <button disabled={busy} onClick={submit} className="px-4 py-2 bg-cyan-600 hover:bg-cyan-500 disabled:opacity-40 text-white rounded text-sm font-semibold" data-testid="bank-deposit-submit">{busy ? 'Depositing…' : 'Deposit'}</button>
        </div>
      </div>
    </div>
  );
};

const WithdrawalRow = ({ w, act }) => (
  <tr className="border-t border-slate-700 align-top" data-testid={`bank-wd-row-${w.id}`}>
    <td className="py-2 text-xs">{new Date(w.created_at).toLocaleString()}<p className="text-slate-500">{w.email}</p></td>
    <td className="text-xs">{w.method.toUpperCase()} {money(w.net)}<p className="text-emerald-400">Fee {money(w.fee_usd)}</p></td>
    <td className="text-xs">{w.recipient.name_on_account}<p className="text-slate-500 font-mono">{w.recipient.routing_number} / {w.recipient.account_number}</p></td>
    <td><SwapStatus s={w.status} />{w.error && <p className="text-xs text-red-400 max-w-xs">{w.error}</p>}</td>
    <td className="space-x-1 whitespace-nowrap text-right">{w.status === 'pending' && <>
      <button onClick={() => act(`withdrawals/${w.id}/approve`)} className="px-2 py-1 bg-emerald-600 text-white rounded text-xs" data-testid={`bank-wd-approve-${w.id}`}>Approve</button>
      <button onClick={() => act(`withdrawals/${w.id}/reject`, {})} className="px-2 py-1 bg-slate-600 text-white rounded text-xs" data-testid={`bank-wd-reject-${w.id}`}>Reject</button></>}</td>
  </tr>
);

export const AdminBankPanel = ({ token }) => {
  const [d, setD] = useState(null);
  const [wds, setWds] = useState([]);
  const [depositSub, setDepositSub] = useState(null);
  const [showPicker, setShowPicker] = useState(false);
  const h = { headers: { Authorization: `Bearer ${token}` } };
  const load = useCallback(() => {
    const hh = { headers: { Authorization: `Bearer ${token}` } };
    axios.get(`${API}/overview`, hh).then(r => setD(r.data)).catch(() => {});
    axios.get(`${API}/withdrawals`, hh).then(r => setWds(r.data.withdrawals)).catch(() => {});
  }, [token]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!wds.some(w => w.status === 'executing')) return undefined;
    const t = setInterval(load, 6000); return () => clearInterval(t);
  }, [wds, load]);
  const act = async (path, body = {}) => {
    try { const r = await axios.post(`${API}/${path}`, body, h); toast.success(r.data.message || 'Done'); } catch (e) { toast.error(e.response?.data?.detail?.toString() || 'Failed'); }
    load();
  };
  if (!d) return <p className="text-slate-400 text-sm">Loading bank…</p>;

  return (
    <div className="space-y-6" data-testid="admin-bank-panel">
      <StatementEmailsAdmin token={token} />
      <BankMasterCard token={token} master={d.master} totals={d.totals} act={act} />
      <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-white font-semibold flex items-center gap-2"><Building2 className="w-5 h-5 text-emerald-400" /> Subscriber accounts</h3>
          <div className="flex items-center gap-3">
            <button onClick={() => setShowPicker(true)} className="text-xs bg-cyan-600 hover:bg-cyan-500 text-white px-3 py-1.5 rounded flex items-center gap-1 font-semibold" data-testid="bank-deposit-open"><Building2 className="w-3.5 h-3.5" /> Deposit BETH USD</button>
            <button onClick={() => act('sync-all')} className="text-xs text-slate-300 hover:text-white flex items-center gap-1" data-testid="bank-sync-all"><RefreshCw className="w-3.5 h-3.5" /> Sync all</button>
          </div>
        </div>
        <div className="overflow-x-auto"><table className="w-full text-sm text-slate-300">
          <thead><tr className="text-left text-xs text-slate-500"><th>Subscriber</th><th>Account / routing</th><th>Bank</th><th>BETH USD</th><th>Status</th><th /></tr></thead>
          <tbody>{d.subscribers.map(s => <SubRow key={s.client_id} s={s} act={act} onDeposit={setDepositSub} />)}</tbody>
        </table></div>
      </div>
      {depositSub && <DepositModal sub={depositSub} token={token} onClose={() => setDepositSub(null)} onDone={load} />}
      {showPicker && <DepositModal subscribers={d.subscribers} token={token} onClose={() => setShowPicker(false)} onDone={load} />}
      <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 space-y-3" data-testid="bank-withdrawals-admin">
        <h3 className="text-white font-semibold">Withdrawals ({d.pending_withdrawals} pending)</h3>
        {!wds.length ? <p className="text-xs text-slate-500">No withdrawals yet.</p> : (
          <div className="overflow-x-auto"><table className="w-full text-sm text-slate-300">
            <thead><tr className="text-left text-xs text-slate-500"><th>When</th><th>Amount</th><th>Recipient</th><th>Status</th><th /></tr></thead>
            <tbody>{wds.map(w => <WithdrawalRow key={w.id} w={w} act={act} />)}</tbody>
          </table></div>
        )}
      </div>
      <BankLedger token={token} refreshKey={d} />
      <OutboundFeedPanel token={token} />
    </div>
  );
};
